/**
 * UnifiedActivationModal Component
 *
 * Displays a consistent confirmation flow for bulk lifecycle activation.
 * Works identically for single and bulk operations, with tiered complexity
 * based on whether supersessions will occur.
 *
 * Tiered UX:
 * - No supersessions: Simple Modal.confirm handled by caller
 * - Has supersessions: This modal with summary, opt-out checkboxes, timeline drill-down
 *
 * Features:
 * - Summary bar showing counts (ready, supersession, failed)
 * - Supersession list with opt-out checkboxes
 * - "View Timeline" button opens SplicingPreviewModal for drill-down
 * - Dynamic button count updates based on selections
 * - Responsive layout via CSS container queries
 */

import { useState, useMemo, useCallback } from 'react'
import { Modal, Space, Typography, Checkbox, Button, Alert, Tooltip, Badge, Progress } from 'antd'
import {
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import type { FieldMetadata, ComparisonConfig, DisplayMetadata } from '@/services/metadataService'
import type { BulkActivationPreview, BulkActivationSupersessionRecord } from '../types'
import type { SplicingText } from '@/pages/modules/data/types'
import { SplicingPreviewModal } from '@/pages/modules/components/SplicingPreviewModal'

import './UnifiedActivationModal.css'

const { Text, Title } = Typography

/**
 * Format a date for display
 */
function formatDate(date: string | null | undefined): string {
  if (!date) return 'Immediate'
  return dayjs(date).format('MMM D, YYYY')
}

/**
 * Format a date range
 */
function formatDateRange(from: string | null, to: string | null): string {
  const fromStr = formatDate(from)
  const toStr = to ? formatDate(to) : 'ongoing'
  return `${fromStr} - ${toStr}`
}

/**
 * Get a key field value from a record for display
 */
function getKeyFieldValue(
  record: Record<string, unknown>,
  keyField: string,
  fieldMetadata?: Record<string, FieldMetadata>
): string {
  const value = record[keyField]
  if (value === null || value === undefined) return '-'

  const meta = fieldMetadata?.[keyField]

  // Format as currency if display_format suggests it
  if (meta?.display_format) {
    const format = meta.display_format.toLowerCase()
    if (format.includes('currency') || format.includes('dollar')) {
      return `$${Number(value).toFixed(2)}`
    }
    if (format.includes('percent')) {
      return `${Number(value).toFixed(1)}%`
    }
  }

  // Auto-detect price/cost fields
  if (typeof value === 'number') {
    if (keyField.toLowerCase().includes('price') || keyField.toLowerCase().includes('cost')) {
      return `$${value.toFixed(2)}`
    }
    return String(value)
  }

  return String(value)
}

export interface UnifiedActivationModalProps {
  /** Whether the modal is visible */
  visible: boolean
  /** Preview data from useBulkActivationPreview hook */
  previewData: BulkActivationPreview | null
  /** Whether fetch is still in progress */
  isLoading?: boolean
  /** Progress during fetch */
  progress?: { total: number; completed: number }
  /** Entity label for messages (e.g., 'offerings') */
  entityLabel?: string
  /** Singular entity label (e.g., 'Offering') */
  entityLabelSingular?: string
  /** Field metadata for formatting */
  fieldMetadata?: Record<string, FieldMetadata>
  /** Comparison config from metadata */
  comparison?: ComparisonConfig
  /** Display config from metadata */
  display?: DisplayMetadata
  /** API endpoint for timeline drill-down */
  endpoint?: string
  /** Toggle selection for a supersession record */
  onToggleSelection: (uid: string) => void
  /** Select all supersession records */
  onSelectAll: () => void
  /** Deselect all supersession records */
  onDeselectAll: () => void
  /** Get UIDs to activate */
  getActivationUids: () => string[]
  /** Get count of records to activate */
  getActivationCount: () => number
  /** Callback when user confirms activation */
  onConfirm: (uids: string[]) => void
  /** Callback when user cancels */
  onCancel: () => void
}

/**
 * UnifiedActivationModal Component
 */
export function UnifiedActivationModal({
  visible,
  previewData,
  isLoading = false,
  progress,
  entityLabel = 'records',
  entityLabelSingular = 'Record',
  fieldMetadata,
  comparison,
  display,
  endpoint = '',
  onToggleSelection,
  onSelectAll,
  onDeselectAll,
  getActivationUids,
  getActivationCount,
  onConfirm,
  onCancel,
}: UnifiedActivationModalProps) {
  // State for timeline drill-down
  const [timelineRecord, setTimelineRecord] = useState<BulkActivationSupersessionRecord | null>(null)
  const [showTimeline, setShowTimeline] = useState(false)

  // Derive key field from comparison config (first key field)
  const keyField = comparison?.keyFields?.[0] ?? 'price'

  // Computed values
  const activationCount = getActivationCount()
  const totalWithSupersessions = previewData?.total ?? 0

  // Counts for summary
  const readyCount = previewData?.readyRecords.length ?? 0
  const supersessionCount = previewData?.supersessionRecords.length ?? 0
  const selectedSupersessionCount = previewData?.supersessionRecords.filter(r => r.selected).length ?? 0
  const failedCount = previewData?.failedRecords.length ?? 0

  // Are all supersession records selected?
  const allSelected = useMemo(() => {
    if (!previewData || previewData.supersessionRecords.length === 0) return true
    return previewData.supersessionRecords.every(r => r.selected)
  }, [previewData])

  // Handle checkbox toggle
  const handleToggle = useCallback((uid: string) => {
    onToggleSelection(uid)
  }, [onToggleSelection])

  // Handle "View Timeline" click
  const handleViewTimeline = useCallback((record: BulkActivationSupersessionRecord) => {
    setTimelineRecord(record)
    setShowTimeline(true)
  }, [])

  // Handle timeline modal close
  const handleTimelineClose = useCallback(() => {
    setShowTimeline(false)
    setTimelineRecord(null)
  }, [])

  // Handle timeline confirmation (activates that single record from drill-down)
  // We don't actually execute from here - just close the timeline preview
  const handleTimelineConfirm = useCallback(() => {
    // For drill-down, we don't execute - user will confirm from main modal
    setShowTimeline(false)
    setTimelineRecord(null)
  }, [])

  // Handle main modal confirm
  const handleConfirm = useCallback(() => {
    const uids = getActivationUids()
    onConfirm(uids)
  }, [getActivationUids, onConfirm])

  // Select all / deselect all toggle
  const handleToggleAll = useCallback(() => {
    if (allSelected) {
      onDeselectAll()
    } else {
      onSelectAll()
    }
  }, [allSelected, onSelectAll, onDeselectAll])

  // Build splicing text for timeline modal
  const splicingText: SplicingText | null = useMemo(() => ({
    modalTitle: `Timeline Preview: Activate ${entityLabelSingular}`,
    modalSubtitle: 'Preview of timeline changes for this record:',
    newVersionLabel: 'New Version',
    currentVersionLabel: 'Current Active Version',
    continuationLabel: 'Resumed Version',
    continuationOptionLabel: 'Resume previous configuration after this version ends',
    continuationDescription: 'A resumed version will maintain the original configuration',
    noContinuationOptionLabel: 'End without continuation',
    noContinuationWarning: `This ${entityLabelSingular.toLowerCase()} will have no active configuration`,
    gapWarningTitle: 'Coverage Gap',
    gapWarningBullet1: 'No active version will exist',
    gapWarningBullet2: 'No active settings will exist',
    confirmButtonLabel: 'Close Preview',
  }), [entityLabelSingular])

  // Loading state - show progress
  if (isLoading && progress) {
    const percent = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0
    return (
      <Modal
        open={visible}
        title="Fetching Previews..."
        footer={null}
        onCancel={onCancel}
        width={500}
        closable={false}
        maskClosable={false}
      >
        <div className="unified-activation-loading">
          <Progress percent={percent} status="active" />
          <Text type="secondary">
            Checking {progress.completed} of {progress.total} {entityLabel}...
          </Text>
        </div>
      </Modal>
    )
  }

  // No preview data
  if (!previewData) {
    return null
  }

  return (
    <>
      <Modal
        open={visible && !showTimeline}
        wrapClassName="unified-activation-modal"
        title={
          <div className="unified-activation-header">
            <div className="unified-activation-header-left">
              <WarningOutlined style={{ color: 'var(--rp-temporal-orange)', fontSize: 20 }} />
              <span className="unified-activation-title">
                Activate {totalWithSupersessions} {entityLabel}?
              </span>
            </div>
            <Badge
              count={totalWithSupersessions}
              style={{ backgroundColor: 'var(--rp-blue-primary)' }}
            />
          </div>
        }
        onCancel={onCancel}
        width={700}
        footer={
          <div className="unified-activation-footer">
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleConfirm}
              disabled={activationCount === 0}
            >
              Activate {activationCount} {activationCount !== totalWithSupersessions ? `of ${totalWithSupersessions}` : ''} {entityLabel}
            </Button>
          </div>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Summary Alert */}
          <Alert
            type="info"
            icon={<InfoCircleOutlined />}
            message={
              <span>
                This will activate <strong>{activationCount}</strong> {entityLabel}
                {supersessionCount > 0 && (
                  <>, <strong>{selectedSupersessionCount}</strong> of which have scheduling conflicts with active versions</>
                )}
                .
              </span>
            }
          />

          {/* Summary Counts */}
          <div className="unified-activation-summary">
            <div className="summary-item summary-ready">
              <CheckCircleOutlined />
              <span className="summary-label">Ready</span>
              <span className="summary-count">{readyCount}</span>
            </div>
            <div className="summary-item summary-supersession">
              <ClockCircleOutlined />
              <span className="summary-label">Have Conflicts</span>
              <span className="summary-count">{supersessionCount}</span>
            </div>
            {failedCount > 0 && (
              <div className="summary-item summary-failed">
                <CloseCircleOutlined />
                <span className="summary-label">Failed</span>
                <span className="summary-count">{failedCount}</span>
              </div>
            )}
          </div>

          {/* Supersession Records with Opt-out */}
          {supersessionCount > 0 && (
            <div className="unified-activation-supersessions">
              <div className="supersessions-header">
                <Title level={5} style={{ margin: 0 }}>
                  Records with Conflicts
                </Title>
                <Button
                  type="link"
                  size="small"
                  onClick={handleToggleAll}
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </Button>
              </div>

              <div className="supersessions-list">
                {previewData.supersessionRecords.map((record) => {
                  const keyValue = record.preview?.newRecord
                    ? getKeyFieldValue(record.preview.newRecord as Record<string, unknown>, keyField, fieldMetadata)
                    : '-'

                  return (
                    <div
                      key={record.uid}
                      className={`supersession-item ${record.selected ? '' : 'excluded'}`}
                    >
                      <Checkbox
                        checked={record.selected}
                        onChange={() => handleToggle(record.uid)}
                      />
                      <div className="supersession-details">
                        <div className="supersession-name">
                          {record.name}
                        </div>
                        <div className="supersession-meta">
                          <span className="supersession-dates">
                            {formatDateRange(record.effectiveFrom, record.effectiveTo)}
                          </span>
                          {keyValue !== '-' && (
                            <>
                              <span className="supersession-separator">|</span>
                              <span className="supersession-key-value">
                                {comparison?.keyFields?.[0] ? `${fieldMetadata?.[keyField]?.title ?? keyField}: ` : ''}
                                {keyValue}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <Tooltip title="View timeline impact">
                        <Button
                          type="link"
                          size="small"
                          icon={<FieldTimeOutlined />}
                          onClick={() => handleViewTimeline(record)}
                        >
                          View Timeline
                        </Button>
                      </Tooltip>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Failed Records Warning */}
          {failedCount > 0 && (
            <Alert
              type="warning"
              message={`${failedCount} ${entityLabel} could not be previewed and will be skipped`}
              description={
                <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                  {previewData.failedRecords.slice(0, 5).map(record => (
                    <li key={record.uid}>
                      <strong>{record.name}</strong>: {record.error}
                    </li>
                  ))}
                  {previewData.failedRecords.length > 5 && (
                    <li>...and {previewData.failedRecords.length - 5} more</li>
                  )}
                </ul>
              }
            />
          )}
        </Space>
      </Modal>

      {/* Timeline Drill-down Modal */}
      {timelineRecord && (
        <SplicingPreviewModal
          visible={showTimeline}
          previewData={timelineRecord.preview}
          splicingText={splicingText}
          loading={false}
          fieldMetadata={fieldMetadata}
          keyFields={comparison?.keyFields}
          timelineLabelField={comparison?.timelineLabelField}
          showNewPrefix={comparison?.showNewPrefix}
          entityLabel={entityLabelSingular}
          titleFields={display?.title_fields}
          historyEndpoint={endpoint.replace(/^\//, '')}
          keyComboHash={timelineRecord.preview?.newRecord?.keyComboHash as string | undefined}
          onConfirm={handleTimelineConfirm}
          onCancel={handleTimelineClose}
        />
      )}
    </>
  )
}

export default UnifiedActivationModal
